import { Alert, Badge, Button, EmptyState, Field, Input } from '@personachat/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, Plus } from 'lucide-react';
import { useEffect, useId, useState, type FormEvent } from 'react';
import { Outlet, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { api } from '../../api/client';
import { lire, messageDe } from '../../api/call';
import type { components } from '../../api/schema';
import { ErreurChargement, Select } from '../../components/bits';
import { DataTable, type Colonne } from '../../components/DataTable';
import { Modal } from '../../components/Modal';
import { useToast } from '../../components/Toast';
import { depuisSaisieParis, formatDateTime, formatNombre } from '../../lib/format';
import { LIBELLE_TYPE, STATUT_EVENEMENT, TON_TYPE } from '../../lib/labels';
import { useSession } from '../../auth/AuthProvider';
import { PageHeader } from '../../pages/PageHeader';

export type Evenement = components['schemas']['EventListItem'];
export type TypeEvenement = 'GUEST' | 'CHALLENGE3';
export const TYPES_EVENEMENT: TypeEvenement[] = ['GUEST', 'CHALLENGE3'];

/** Tous les événements, du plus récent au plus ancien (liste, choix d'un événement pour une clé). */
export function useEvenements() {
  return useQuery({ queryKey: ['evenements'], queryFn: async () => (await lire(api.GET('/api/admin/events'))).events, staleTime: 30_000 });
}

export function useModules() {
  return useQuery({ queryKey: ['modules'], queryFn: async () => (await lire(api.GET('/api/admin/modules'))).modules, staleTime: 300_000 });
}

export function StatutEvenement({ statut }: { statut: Evenement['status'] }) {
  const s = STATUT_EVENEMENT[statut];
  return <Badge tone={s.ton}>{s.libelle}</Badge>;
}

/** « 01/06/2026 22:00 → 15/06/2026 22:00 », « Dès maintenant », « Sans fin ». */
export function periode(e: { startsAt: string | null; endsAt: string | null }): string {
  const debut = e.startsAt ? formatDateTime(e.startsAt) : 'Dès l’inscription';
  const fin = e.endsAt ? formatDateTime(e.endsAt) : 'sans fin';
  return `${debut} → ${fin}`;
}

/**
 * Événements (cahier des charges §7.6) : Discoveur pour les comptes Invité,
 * Immersion pour les comptes CHALLENGE3. Module, calendrier, clé de
 * prévisualisation et fin se règlent ici au lieu d'être écrits dans le code.
 */
export function EventsPage() {
  const navigate = useNavigate();
  const { id: ouvert } = useParams();
  const [params, setParams] = useSearchParams();
  const evenements = useEvenements();
  const superAdmin = !!useSession().user?.isSuperAdmin;
  const creer = superAdmin && params.get('creer') === '1';
  const ouvrirCreation = (o: boolean) =>
    setParams(
      (a) => {
        const s = new URLSearchParams(a);
        if (o) s.set('creer', '1');
        else s.delete('creer');
        return s;
      },
      { replace: true }
    );

  const colonnes: Colonne<Evenement>[] = [
    {
      id: 'nom',
      header: 'Événement',
      cell: (e) => (
        <div>
          <div className="cell-main">{e.name}</div>
          <div className="cell-sub">Module : {e.module.name}</div>
        </div>
      ),
    },
    { id: 'type', header: 'Comptes', cell: (e) => <Badge tone={TON_TYPE[e.userType]}>{LIBELLE_TYPE[e.userType]}</Badge> },
    { id: 'dates', header: 'Dates (Paris)', cell: (e) => <span className="cell-sub">{periode(e)}</span> },
    { id: 'statut', header: 'Statut', cell: (e) => <StatutEvenement statut={e.status} /> },
    {
      id: 'fin',
      header: 'Après la fin',
      secondaire: true,
      cell: (e) => (e.endBehavior === 'BLOCK' ? <Badge tone="terracotta">Bloqué</Badge> : <span className="muted">Reste ouvert</span>),
    },
    { id: 'inscrites', header: 'Inscrites', align: 'right', cell: (e) => <span className="num">{formatNombre(e.stats.registered)}</span> },
    { id: 'normal', header: 'Passées en Normal', align: 'right', secondaire: true, cell: (e) => <span className="num">{formatNombre(e.stats.movedToNormal)}</span> },
    { id: 'cles', header: 'Clés', align: 'right', cell: (e) => <span className="num">{formatNombre(e._count.accessKeys)}</span> },
  ];

  return (
    <>
      <PageHeader
        title="Événements"
        lead="Discoveur (comptes Invité) et Immersion (comptes Immersion 3) : module ouvert, calendrier des exercices, prévisualisation et fin."
        actions={
          superAdmin && (
            <Button variant="primary" icon={<Plus size={15} aria-hidden="true" />} onClick={() => ouvrirCreation(true)}>
              Créer un événement
            </Button>
          )
        }
      />
      {evenements.isError ? (
        <ErreurChargement erreur={evenements.error} onReessayer={() => void evenements.refetch()} />
      ) : (
        <DataTable
          libelle="Événements"
          colonnes={colonnes}
          lignes={evenements.data}
          cleLigne={(e) => e.id}
          chargement={evenements.isFetching && !evenements.data}
          onOuvrir={(e) => navigate(`/evenements/${e.id}`)}
          estActive={(e) => e.id === ouvert}
          vide={
            <EmptyState
              icon={<CalendarClock size={28} aria-hidden="true" />}
              title="Aucun événement"
              text="Sans événement, l’application ouvre les modules « Module Discoveur » et « Immersion 3 » par leur nom."
            />
          }
        />
      )}
      <p className="note">
        Une nouvelle clé Invité ou Immersion se rattache à l’événement le plus récent de son type, sauf choix contraire à sa création. Une élève
        suit l’événement de sa clé d’inscription.
      </p>
      <CreerEvenement open={creer} onOpenChange={ouvrirCreation} />
      <Outlet />
    </>
  );
}

function CreerEvenement({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const id = useId();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const modules = useModules();
  const [nom, setNom] = useState('');
  const [type, setType] = useState<TypeEvenement | ''>('');
  const [moduleId, setModuleId] = useState('');
  const [debut, setDebut] = useState('');
  const [fin, setFin] = useState('');
  const [touche, setTouche] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [erreurApi, setErreurApi] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setNom('');
      setType('');
      setModuleId('');
      setDebut('');
      setFin('');
      setTouche(false);
      setErreurApi(null);
    }
  }, [open]);

  const debutIso = debut ? depuisSaisieParis(debut) : null;
  const finIso = fin ? depuisSaisieParis(fin) : null;
  const erreurs = {
    nom: nom.trim().length >= 2 ? null : '2 caractères au moins.',
    type: type ? null : 'Choisissez le type de comptes.',
    module: moduleId ? null : 'Choisissez le module ouvert aux inscrites.',
    fin: debutIso && finIso && finIso <= debutIso ? 'La fin doit être après le début.' : null,
  };
  const valide = Object.values(erreurs).every((e) => e === null);

  const creer = async (e: FormEvent) => {
    e.preventDefault();
    setTouche(true);
    if (!valide || !type) return;
    setEnvoi(true);
    setErreurApi(null);
    try {
      const r = await lire(
        api.POST('/api/admin/events', {
          body: { name: nom.trim(), userType: type, moduleId, startsAt: debutIso, endsAt: finIso, endBehavior: 'OPEN' },
        })
      );
      await queryClient.invalidateQueries({ queryKey: ['evenements'] });
      toast.succes(`Événement « ${r.event.name} » créé : réglez maintenant son calendrier.`);
      navigate(`/evenements/${r.event.id}?onglet=calendrier`);
    } catch (err) {
      setErreurApi(messageDe(err));
    } finally {
      setEnvoi(false);
    }
  };

  const e = (c: keyof typeof erreurs) => (touche ? erreurs[c] : null);

  return (
    <Modal open={open} onOpenChange={(o) => !envoi && onOpenChange(o)} title="Créer un événement" width={600}>
      <form className="stack" onSubmit={creer} noValidate>
        <Field id={`${id}-nom`} label="Nom" error={e('nom')}>
          <Input id={`${id}-nom`} value={nom} onChange={(ev) => setNom(ev.target.value)} maxLength={80} placeholder="Ex. : Immersion 4" invalid={!!e('nom')} autoFocus />
        </Field>
        <fieldset className="choix">
          <legend className="ui-field__label">Comptes concernés</legend>
          {TYPES_EVENEMENT.map((t) => (
            <label key={t} className={t === type ? 'choix__option choix__option--active' : 'choix__option'}>
              <input type="radio" name={`${id}-type`} checked={t === type} onChange={() => setType(t)} />
              <span>
                <strong>{LIBELLE_TYPE[t]}</strong>
                <span className="note">
                  {t === 'GUEST' ? 'Discoveur : inscription avec une clé Invité.' : 'Immersion : inscription avec une clé Immersion 3.'} Ne change plus
                  ensuite.
                </span>
              </span>
            </label>
          ))}
          {e('type') && <span className="ui-field__error">{e('type')}</span>}
        </fieldset>
        <Field id={`${id}-module`} label="Module ouvert aux inscrites" error={e('module')}>
          <Select id={`${id}-module`} value={moduleId} onChange={(ev) => setModuleId(ev.target.value)}>
            <option value="">Choisir un module…</option>
            {(modules.data ?? []).map((m) => (
              <option key={m.id} value={m.id}>
                {m.name} ({formatNombre(m._count.personalities)} coach{m._count.personalities > 1 ? 's' : ''}){m.isActive ? '' : ' — inactif'}
              </option>
            ))}
          </Select>
        </Field>
        <div className="grid-2">
          <Field id={`${id}-debut`} label="Début (heure de Paris)" hint="Vide : dès l’inscription.">
            <Input id={`${id}-debut`} type="datetime-local" value={debut} onChange={(ev) => setDebut(ev.target.value)} />
          </Field>
          <Field id={`${id}-fin`} label="Fin (heure de Paris)" hint="Vide : sans fin." error={e('fin')}>
            <Input id={`${id}-fin`} type="datetime-local" value={fin} onChange={(ev) => setFin(ev.target.value)} invalid={!!e('fin')} />
          </Field>
        </div>
        <p className="note">Le calendrier des coachs, l’écran de fin et la clé de prévisualisation se règlent ensuite dans la fiche.</p>
        {erreurApi && <Alert>{erreurApi}</Alert>}
        <div className="modal__footer modal__footer--inline">
          <Button onClick={() => onOpenChange(false)} disabled={envoi}>
            Annuler
          </Button>
          <Button type="submit" variant="primary" loading={envoi}>
            Créer l’événement
          </Button>
        </div>
      </form>
    </Modal>
  );
}
